import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  TransactionAccount,
  TransactionAccountProduct,
  InvoiceGroupProduct,
  BillingFrequency,
} from '@css-xp/graphtypes/src/generated-types-new';
import { STATUSPAGE_PATH_2 } from '@atlassian/cssxp-typescommon';
import capitalize from 'lodash/capitalize';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import VerticalMoreIcon from '@atlaskit/icon/glyph/more-vertical';
import { useTransactionAccountContext, useApplicationContext } from '../../common-context';
import { FormattedAddress } from '../../formatted';
import { CloudProductDetailView, PaymentMethodView, NextBillExpiryDate } from '../../entitlement-utils';
import { NoDataContentInTable } from '../../no-data-content-table';
import { getBilledForColumnData } from '../../entitlement-utils/src/utils';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { CustomDynamicResultTable } from '../../custom-dynamic-result-table';
import { ColGroup, TableBody } from '../../search-results-table';
import { useFeatureToggle, useSidebar } from '../../hooks';
import { isBilledElsewhereProduct } from '@atlassian/cssxp-utils-product';
// eslint-disable-next-line
import { BilledElsewhereBadge } from '../../../account-subscriptions/entitlements-v2/common/components/billed-elsewhere-badge';

export interface ProductAppSubsriptionsTableProps {
  data: Array<TransactionAccountProduct>;
  billingProfileId?: string | undefined;
}

export const ProductAppSubsriptionsTable = ({ data, billingProfileId = '' }: ProductAppSubsriptionsTableProps) => {
  const applicationContext = useApplicationContext();
  const [isEnabled] = useFeatureToggle();
  const isPartnerAccount = applicationContext?.partnerAccount;
  const { openSidebar } = useSidebar();
  const totalCount = useMemo(() => data?.length ?? 0, [data]);
  const headData = useMemo(() => {
    const columnsForTxa = [
      {
        name: 'Subscription',
        customClassNames: 'col-30',
      },

      {
        name: 'Usage',
        customClassNames: 'col-10',
      },

      {
        name: 'Next bill/ Expiry',
        customClassNames: 'col-10',
      },

      {
        name: 'Billing cycle',
        customClassNames: 'col-10',
      },

      {
        customClassNames: 'col-15',
        name: 'Payment method',
      },

      {
        customClassNames: 'col-20',
        name: 'Sold-to address',
      },
      {
        name: '',
        customClassNames: 'col-5',
      },
    ];

    const columnsForBillingProfile = [
      {
        name: 'Subscription',
        customClassNames: 'col-40',
      },

      {
        name: 'Usage',
        customClassNames: 'col-15',
      },
      {
        name: 'Billing cycle',
        customClassNames: 'col-15',
      },
      {
        name: 'Next bill/ Expiry',
        customClassNames: 'col-20',
      },
      {
        name: '',
        customClassNames: 'col-10',
      },
    ];

    return billingProfileId ? columnsForBillingProfile : columnsForTxa;
  }, []);

  const { data: transactionAccountData, getTransactionAccount } = useTransactionAccountContext();
  const transactionAccount = billingProfileId ? null : getTransactionAccount(transactionAccountData);
  const history = useHistory();

  const getRowActionForTxa = useCallback(
    (product: TransactionAccountProduct) => {
      if (isPartnerAccount) {
        return null;
      }

      return (
        <DropdownMenu
          position="bottom right"
          triggerType="button"
          triggerButtonProps={{
            appearance: 'subtle',
            className: 'trigger-button',
            iconBefore: <VerticalMoreIcon label="invoice-action" />,
          }}
          testId={`actions-${product?.id}`}
        >
          <DropdownItemGroup>
            <DropdownItem
              key={'add-promotion'}
              onClick={() => {
                history.push(`${STATUSPAGE_PATH_2}/${encodeURIComponent(product?.id)}`);
              }}
            >
              View details
            </DropdownItem>
          </DropdownItemGroup>
        </DropdownMenu>
      );
    },
    [history]
  );

  const getRowActionForBillingProfile = useCallback(
    (product: InvoiceGroupProduct) => {
      return (
        <DropdownMenu
          position="bottom right"
          triggerType="button"
          triggerButtonProps={{
            appearance: 'subtle',
            className: 'trigger-button',
            iconBefore: <VerticalMoreIcon label="invoice-action" />,
          }}
          testId={`actions-${product?.id}`}
        >
          <DropdownItemGroup>
            <DropdownItem
              key={'add-promotion'}
              onClick={() => {
                history.push(`${STATUSPAGE_PATH_2}/${encodeURIComponent(product?.id)}`);
              }}
            >
              View details
            </DropdownItem>
          </DropdownItemGroup>
        </DropdownMenu>
      );
    },
    [billingProfileId]
  );

  const getSoldToAddress = useCallback(
    (invoiceGroupId: string | undefined | null) => {
      let currentInvoiceGroupDetails;
      if (!invoiceGroupId) {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes[0];
      } else {
        currentInvoiceGroupDetails = transactionAccount?.availableInvoiceGroups?.nodes?.find(
          (invoiceGroupObj) => invoiceGroupObj?.id === invoiceGroupId
        );
      }

      if (currentInvoiceGroupDetails?.shipToParty) {
        return (
          <div className="line-clamp-sold-to-address">
            <FormattedAddress
              addressLine1={currentInvoiceGroupDetails.shipToParty?.addressLine1}
              addressLine2={currentInvoiceGroupDetails.shipToParty?.addressLine2}
              city={currentInvoiceGroupDetails.shipToParty?.city}
              country={currentInvoiceGroupDetails.shipToParty?.country}
              postcode={currentInvoiceGroupDetails.shipToParty?.postcode}
              state={currentInvoiceGroupDetails.shipToParty?.state}
              organizationName={currentInvoiceGroupDetails.shipToParty?.name}
            />
          </div>
        );
      }
      return 'Same as billing address';
    },
    [transactionAccount]
  );

  const getColumnDataForTxa = (product: TransactionAccountProduct) => {
    const billedElsewhere = isBilledElsewhereProduct(product?.productId);
    const entitlementNumber = product?.entitlementNumber || '';

    const isBillingAnnual = product?.billingFrequency === BillingFrequency.ANNUAL;

    return (
      <>
        <td>
          <CloudProductDetailView
            logoId={product?.logoId}
            productId={product?.productId}
            entitlementNumber={entitlementNumber}
            trial={product?.trial ?? false}
            billingStatus={product?.billingStatus}
            provisioningStatus={product?.provisioningStatus}
            edition={product?.edition}
            productName={product?.productName}
            upgradeTrial={product?.upgradeTrial}
            isSandbox={product?.isSandbox ?? false}
            {...(isPartnerAccount && { productLink: product?.id || entitlementNumber })}
            scheduledChanges={product?.scheduledChanges}
          />
        </td>
        {billedElsewhere ? (
          <td style={{ position: 'unset' }}>
            <BilledElsewhereBadge className="opsgenie-billing-account" />
          </td>
        ) : (
          <>
            <td>
              <div>
                {product?.activeUsers !== undefined && product?.activeUsers !== null && (
                  <div>{capitalize(getBilledForColumnData(product?.productId, product?.edition))}</div>
                )}

                <div>{product?.activeUsers ?? '-'}</div>
              </div>
            </td>
            <td>
              <NextBillExpiryDate item={product} />
            </td>
            <td>
              <p>{product?.billingFrequency ? capitalize(product.billingFrequency) : <NoDataContentInTable />}</p>
              {isBillingAnnual && product?.userTier && <p>User tier - {product?.userTier}</p>}
            </td>
            <td>
              <PaymentMethodView
                invoiceGroupId={product?.billingGroupId}
                transactionAccount={transactionAccount as TransactionAccount}
              />
            </td>
            <td>{getSoldToAddress(product?.billingGroupId)}</td>
            <td style={{ overflow: 'visible' }} data-testid="row-action-txa">
              {getRowActionForTxa(product)}
            </td>
          </>
        )}
      </>
    );
  };

  const getColumnDataForBillingProfile = (product: InvoiceGroupProduct) => {
    const billedElsewhere = isBilledElsewhereProduct(product?.productId);
    const isBillingAnnual = product?.billingFrequency === BillingFrequency.ANNUAL;

    return (
      <>
        <td>
          <CloudProductDetailView
            logoId={product?.logoId as string}
            productId={product?.productId as string}
            entitlementNumber={product?.entitlementNumber || ''}
            trial={product?.trial ?? false}
            billingStatus={product?.billingStatus}
            provisioningStatus={product?.provisioningStatus}
            edition={product?.edition}
            productName={product?.productName}
            isSandbox={product?.isSandbox ?? false}
            upgradeTrial={product?.upgradeTrial}
            scheduledChanges={product?.scheduledChanges}
          />
        </td>
        {billedElsewhere ? (
          <td style={{ position: 'unset' }}>
            <BilledElsewhereBadge className="opsgenie-billing-account" />
          </td>
        ) : (
          <>
            <td>
              <div>
                {product?.activeUsers !== undefined && product?.activeUsers !== null && (
                  <div>{capitalize(getBilledForColumnData(product?.productId, product?.edition))}</div>
                )}

                <div>{product?.activeUsers ?? '-'}</div>
              </div>
            </td>
            <td>
              <p>{product?.billingFrequency ? capitalize(product.billingFrequency) : <NoDataContentInTable />}</p>
              {
                // similar condition exists in back-end, check - packages/services/account-subscription/src/api/atlassian-business-graph/schemas/billing/invoice-group/invoice-group-resolvers.ts
                isBillingAnnual && product?.userTier && <p>User tier - {product?.userTier}</p>
              }
            </td>
            <td>{<NextBillExpiryDate item={product} />}</td>
            <td style={{ overflow: 'visible' }}>{getRowActionForBillingProfile(product)}</td>
          </>
        )}
      </>
    );
  };

  const rowData = useMemo(() => {
    return data.map((product: TransactionAccountProduct, index: number) => {
      return (
        <tr key={index}>
          {billingProfileId
            ? getColumnDataForBillingProfile(product as InvoiceGroupProduct)
            : getColumnDataForTxa(product)}
        </tr>
      );
    });
  }, [data]);

  return (
    <CustomDynamicResultTable
      isLoading={false}
      error={null}
      noResults={totalCount === 0}
      testId="product-app-subscriptions"
      emptyStateProps={{
        header: '',
        description: NO_DETAILS_TO_SHOW,
      }}
      isPaginationEnabled={false}
      totalCount={totalCount}
      boxShadow={false}
    >
      <ColGroup columns={headData} />
      <TableBody>{rowData}</TableBody>
    </CustomDynamicResultTable>
  );
};
